import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Layouts
import CustomerSatisfactionLoader from "@/Layouts/components/Skeletons/CustomerSatisfactionLoader/CustomerSatisfactionLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Components
import Excellence from "@PayPlans/components/Commons/Excellence";

// MUI
import { Grid } from "@mui/material";

const filterErrorsList = (errors, key) => errors.filter((error) => error !== key);
const getMakes = (makeMetrics = []) => [...makeMetrics].map((element) => element.make);

export default function ExcellenceMakes(props) {
	const {
		sectionId = "sfe",
		reference = "SFE",
		department = "SALES",
		content = {
			makeMetrics: [],
		},
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		useSectionsErrors = () => {},
	} = props;

	// Hooks
	const { errors, getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line no-unused-vars
	const [isValidModels, setIsValidModels] = useState(true);
	const [model, setModel] = useState(null);

	// Error handler
	const tabsErrors = useMemo(() => {
		let localErrors = [];
		const makes = getMakes(content?.makeMetrics);
		if (errors.size) {
			localErrors = makes?.map((make) => {
				const listErrors = errors?.get(sectionId) || [];
				const error = listErrors?.includes(getErrorKey(sectionId, make));
				return Boolean(error);
			});
		}
		return localErrors;
	}, [sectionId, content, errors, getErrorKey]);

	// Actions handlers
	const update = (section, data) => {
		setModel({ ...data });
		onActionUpdate?.(section, null, { makeMetrics: [...data.makeMetrics] });
	};

	const handleUpdate = (section, area, data) => {
		const updatedModel = { ...model };
		removeSectionError(section, area); // Remove the area error

		// Search make metrics and update
		const makeMetrics = (updatedModel && [...updatedModel.makeMetrics]) || [];
		for (const i in makeMetrics) {
			if (makeMetrics[i].make === area) {
				makeMetrics[i] = data;
				break;
			}
		}
		updatedModel["makeMetrics"] = makeMetrics;

		const filteredErrors = filterErrorsList(errors?.get(section) || [], getErrorKey(section, area));
		const isValid = !filteredErrors.length;

		// Update or propagate
		isValid ? update(section, updatedModel) : setModel(updatedModel);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useMemo(() => {
		const sectionErrors = errors?.get(sectionId) || [];
		setIsValidModels(!sectionErrors.length);
	}, [sectionId, errors]);

	useEffect(() => {
		const tabsMakes = getMakes(content?.makeMetrics);
		setModel({
			tabsProps: {
				tabs: [...tabsMakes],
				errors: [],
			},
			makeMetrics: clone(content?.makeMetrics || []),
		});

		return () => {
			setModel(null);
		};
	}, [content]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
			setIsValidModels(true);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader />
						<CustomerSatisfactionLoader title buttons inputs sx={{ padding: "0 32px" }} />
					</Grid>
				) : (
					Boolean(model?.makeMetrics?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={tabsErrors}
							content={Object.entries(model?.makeMetrics)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<Excellence
											reference={reference}
											department={department}
											make={value.make}
											makeMetrics={value}
											readOnly={readOnly}
											onActionUpdate={(make, makeMetrics) => handleUpdate(sectionId, make, makeMetrics)}
											onActionValidation={(make, isValid) => handleValidation(sectionId, make, isValid)}
										/>
									</Grid>
								</Grid>
							))}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
ExcellenceMakes.propTypes = {
	sectionId: PropTypes.string,
	reference: PropTypes.string,
	department: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};
